import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  allProductsById,
  groupsByIdAtom,
  productTocAtom,
} from 'shared/api/masters'
import React, { useState } from 'react'
import { Box, Button, Checkbox, TextField } from '@mui/material'
import { useProductForId } from 'shared/api/tree'

export function ProductMultiSelect({
  onSelect,
  selected,
}: {
  onSelect: (ids: number[]) => void
  selected: number[]
}) {
  const [currentSelection, setCurrentSelection] = useState(selected)
  const [searchTerm, setSearchTerm] = useState('')
  const { groupKeys, grouped } = useAtomValue(productTocAtom)
  const groups = useAtomValue(groupsByIdAtom) ?? {}
  const productsById = useAtomValue(allProductsById) ?? {}

  function toggleSelection(id: number) {
    return () => {
      setCurrentSelection((prev) => {
        if (prev.includes(id)) return prev.filter((i) => i !== id)
        return [...prev, id]
      })
    }
  }

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Search curricula..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ marginBottom: 2 }}
        size="small"
      />
      <Box
        padding={'10px'}
        height={'300px'}
        overflow={'auto'}
        border={'solid 1px'}
      >
        {groupKeys.map((groupKey) => {
          const group = groups[groupKey]
          const productIds = grouped[groupKey]

          return (
            <ProductGroup
              key={groupKey}
              groupName={group?.name ?? 'Not Grouped'}
              productIds={productIds}
              searchTerm={searchTerm}
              currentSelection={currentSelection}
              toggleSelection={toggleSelection}
              productsById={productsById}
            />
          )
        })}
      </Box>
      <Button onClick={() => onSelect(currentSelection)}>Select</Button>
    </Box>
  )
}

function ProductGroup({
  groupName,
  productIds,
  searchTerm,
  currentSelection,
  toggleSelection,
  productsById,
}: {
  groupName: string
  productIds: number[]
  searchTerm: string
  currentSelection: number[]
  toggleSelection: (id: number) => () => void
  productsById: Record<number, { title: string }>
}) {
  const trimmedSearch = searchTerm.trim().toLowerCase()
  const filteredProductIds = productIds.filter((id) => {
    const product = productsById[id]
    if (!product) return false
    if (!trimmedSearch) return true
    return product.title.toLowerCase().includes(trimmedSearch)
  })

  if (filteredProductIds.length === 0) return null

  return (
    <>
      <Box padding="5px">{groupName}</Box>
      {filteredProductIds.map((productId) => (
        <ProductItem
          key={productId}
          productId={productId}
          onCheck={toggleSelection(productId)}
          isSelected={currentSelection.includes(productId)}
        />
      ))}
    </>
  )
}

function ProductItem({
  productId,
  onCheck,
  isSelected,
}: {
  productId: number
  onCheck: () => void
  isSelected: boolean
}) {
  const product = useProductForId(productId)

  if (!product) return null

  return (
    <Box key={productId}>
      <Button onClick={onCheck}>{product.title}</Button>
      <Checkbox checked={isSelected} onChange={onCheck} />
    </Box>
  )
}
